import { type FC } from 'react';

// MUI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

// screen
import { type LayoutProps } from 'modules/map/components/daily-dish/types';
import { getFontFamily } from 'modules/map/components/daily-dish/templates/utils';
import { nonEatingDayLabel, nonEatingLabelFontSize } from 'modules/map/components/daily-dish/utils';
import WeekdayTitle from 'modules/map/components/daily-dish/templates/components/weekday/WeekdayTitle';
import { WeekdayTitlePosition } from 'modules/map/components/daily-dish/constants';

/*
 * Layout 2: Six Columns (1 horizontal row)
 */
export const SixColumnsLayout: FC<LayoutProps> = (props) => {
  const { days, settings, maxCount, getDishes } = props;

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '24px',
        px: 3,
        mb: 10,
      }}
    >
      {days.map(({ weekDay, dishes, afternoons, noMealReason }, index) => {
        const lunchDishes = getDishes(dishes, maxCount.lunch);
        const noMealLabel = nonEatingDayLabel({ dishes, afternoons, noMealReason });
        const isNoMeal = Boolean(noMealLabel);
        const afternoonDishes = getDishes(afternoons, maxCount.afternoon) as (string[] | null)[];
        const cardColor = settings.menu.weekday[weekDay].borderColor;

        // Staggered effect: alternate items up and down
        const isEven = index % 2 === 0;
        const verticalOffset = !isEven ? '-30px' : '30px';

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              transform: `translateY(${verticalOffset})`,
              flex: '0 0 auto',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '280px',
                height: '450px',
                border: `${settings.menu.borderWidth}px solid ${cardColor}`,
                borderRadius: `${settings.menu.borderRadius}px`,
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                letterSpacing: '0.15px',
                p: 1,
                mb: 8,
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  py: 3,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    flex: 1,
                    fontSize: `${settings.font.content.fontSize}px`,
                    color: settings.font.content.fontColor,
                    fontFamily: getFontFamily(settings.font.content.fontFamilyId),
                    lineHeight: 1.4,
                  }}
                >
                  {(isNoMeal ? [noMealLabel] : lunchDishes).map((dish, idx) => (
                    <Typography
                      variant="inherit"
                      key={idx}
                      sx={{
                        userSelect: 'none',
                        color: 'inherit',
                        textAlign: 'center',
                        my: '4px',
                        ...(isNoMeal && {
                          fontSize: nonEatingLabelFontSize(settings.font.content.fontSize),
                        }),
                      }}
                    >
                      {dish ? dish : <>&nbsp;</>}
                    </Typography>
                  ))}

                  {afternoons.length > 0 && (
                    <Divider sx={{ borderBottom: 1, borderColor: cardColor }} />
                  )}

                  {afternoonDishes.map((meal, idx) => {
                    if (!meal) return <>&nbsp;</>;

                    const dishName = (meal[0] || '').replace(/\d\./, '');
                    const manufacturer = meal[1] || '';

                    return (
                      <Typography
                        key={idx}
                        sx={{
                          userSelect: 'none',
                          color: 'inherit',
                          textAlign: 'center',
                        }}
                        variant="inherit"
                      >
                        {`${dishName} ${manufacturer}`}
                      </Typography>
                    );
                  })}
                </Box>
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top:
                    settings.menu.weekdayTitlePosition === WeekdayTitlePosition.TOP
                      ? '-28px'
                      : 'auto',
                  bottom:
                    settings.menu.weekdayTitlePosition === WeekdayTitlePosition.BOTTOM
                      ? '-28px'
                      : 'auto',
                }}
              >
                <WeekdayTitle weekDay={weekDay} settings={settings} layoutId={2} />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SixColumnsLayout;
